import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, GraduationCap, Users } from "lucide-react";

export default function EducationEleves() {
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());

  function empty() {
    return { nom: "", prenom: "", date_naissance: "", classe_id: "none", parent_nom: "", parent_email: "", parent_telephone: "", adresse: "", notes: "" };
  }

  const load = async () => {
    try {
      const [e, c] = await Promise.all([api.get("/chef/education/eleves"), api.get("/chef/education/classes")]);
      setItems(e.data); setClasses(c.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, classe_id: form.classe_id === "none" ? null : form.classe_id };
    try {
      if (editing) { await api.patch(`/chef/education/eleves/${editing.id}`, payload); toast.success("Élève mis à jour"); }
      else { await api.post("/chef/education/eleves", payload); toast.success("Élève ajouté"); }
      setOpen(false); setEditing(null); setForm(empty()); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet élève ? Notes et présences seront aussi supprimées.")) return;
    try { await api.delete(`/chef/education/eleves/${id}`); toast.success("Élève supprimé"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openEdit = (row) => { setEditing(row); setForm({ ...empty(), ...row, classe_id: row.classe_id || "none" }); setOpen(true); };
  const openNew = () => { setEditing(null); setForm(empty()); setOpen(true); };

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow="Éducation · Élèves"
        title="Liste des élèves"
        sub="Gérez votre fichier élève : identité, classe, parents."
        right={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty()); } }}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="eleve-add-btn" className="h-11 bg-[#002FA7] text-white"><Plus className="h-4 w-4 mr-2" /> Ajouter un élève</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle className="font-display text-2xl font-black tracking-tight">{editing ? "Modifier l'élève" : "Nouvel élève"}</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-12 gap-3" data-testid="eleve-form">
                <F label="Nom" value={form.nom} onChange={(v) => setForm({ ...form, nom: v })} span={6} required testid="eleve-nom" />
                <F label="Prénom" value={form.prenom} onChange={(v) => setForm({ ...form, prenom: v })} span={6} required testid="eleve-prenom" />
                <F label="Date de naissance" type="date" value={form.date_naissance} onChange={(v) => setForm({ ...form, date_naissance: v })} span={6} testid="eleve-naissance" />
                <div className="col-span-6">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Classe</Label>
                  <Select value={form.classe_id} onValueChange={(v) => setForm({ ...form, classe_id: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="eleve-classe"><SelectValue placeholder="Aucune" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune classe</SelectItem>
                      {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <F label="Parent — Nom complet" value={form.parent_nom} onChange={(v) => setForm({ ...form, parent_nom: v })} span={6} testid="eleve-parent-nom" />
                <F label="Parent — Email" type="email" value={form.parent_email} onChange={(v) => setForm({ ...form, parent_email: v })} span={6} testid="eleve-parent-email" />
                <F label="Parent — Téléphone" value={form.parent_telephone} onChange={(v) => setForm({ ...form, parent_telephone: v })} span={6} testid="eleve-parent-tel" />
                <F label="Adresse" value={form.adresse} onChange={(v) => setForm({ ...form, adresse: v })} span={6} testid="eleve-adresse" />
                <div className="col-span-12">
                  <Label className="text-xs uppercase tracking-[0.2em] font-semibold">Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="mt-1" data-testid="eleve-notes" />
                </div>
                <DialogFooter className="col-span-12"><Button type="submit" className="bg-[#002FA7] text-white" data-testid="eleve-submit">{editing ? "Mettre à jour" : "Ajouter"}</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="p-6 lg:p-10">
        <div className="bg-white border border-[#DEE2E6]" data-testid="eleves-list">
          {items.length === 0 ? (
            <div className="p-10 text-center text-sm text-[#868E96]">
              <Users className="h-10 w-10 mx-auto mb-3 text-[#DEE2E6]" />
              Aucun élève.
            </div>
          ) : items.map((e) => (
            <div key={e.id} className="p-4 border-b border-[#F8F9FA] last:border-b-0 grid grid-cols-12 gap-3 items-center hover:bg-[#F8F9FA]" data-testid={`eleve-${e.id}`}>
              <div className="col-span-3">
                <div className="font-bold">{e.prenom} {e.nom}</div>
                {e.date_naissance && <div className="text-[10px] text-[#868E96] font-mono">Né(e) le {new Date(e.date_naissance).toLocaleDateString("fr-FR")}</div>}
              </div>
              <div className="col-span-2 text-xs">
                {e.classe_nom ? <span className="px-2 py-0.5 bg-[#002FA7] text-white font-bold">{e.classe_nom}</span> : <span className="text-[#868E96] italic">Sans classe</span>}
              </div>
              <div className="col-span-3 text-xs">
                {e.parent_nom && <div><GraduationCap className="h-3 w-3 inline mr-1" /> {e.parent_nom}</div>}
                {e.parent_email && <div className="text-[#868E96]">{e.parent_email}</div>}
              </div>
              <div className="col-span-2 text-xs text-[#495057]">{e.parent_telephone || "—"}</div>
              <div className="col-span-2 flex justify-end gap-1">
                <Button size="sm" variant="outline" onClick={() => openEdit(e)} data-testid={`eleve-edit-${e.id}`} className="h-7 px-2"><Edit3 className="h-3 w-3" /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(e.id)} data-testid={`eleve-delete-${e.id}`} className="h-7 px-2 border-[#E60000] text-[#E60000]"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, type = "text", span = 12, required, testid }) {
  return (
    <div className={`col-span-${span}`}>
      <Label className="text-xs uppercase tracking-[0.2em] font-semibold">{label}{required && <span className="text-[#E60000]"> *</span>}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} required={required} className="h-11 mt-1" />
    </div>
  );
}
